import { useForm, usePage, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useState, useEffect } from "react"
import type { Team, SharedProps } from "@/types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  team?: Team | null
  organisations: { id: number | string; name: string }[]
  allTeams: { id: number | string; name: string; organisation_id: number | string }[]
}

export function TeamFormDialog({ open, onOpenChange, team, organisations, allTeams }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const isEdit = !!team
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleDelete = () => {
    if (!team) return
    router.delete(`/teams/${team.id}`, {
      onSuccess: () => {
        setShowDeleteConfirm(false)
        onOpenChange(false)
      },
    })
  }

  const { data, setData, post, patch, processing, reset } = useForm({
    name: team?.name || "",
    organisation_id: team?.organisation_id
      ? String(team.organisation_id)
      : current_user?.is_super_admin
        ? ""
        : String(current_user?.organisation_id || ""),
    parent_team_id: team?.parent_team_id ? String(team.parent_team_id) : "",
  })

  useEffect(() => {
    setData({
      name: team?.name || "",
      organisation_id: team?.organisation_id
        ? String(team.organisation_id)
        : current_user?.is_super_admin
          ? ""
          : String(current_user?.organisation_id || ""),
      parent_team_id: team?.parent_team_id ? String(team.parent_team_id) : "",
    })
  }, [team])

  const filteredTeams = data.organisation_id
    ? allTeams.filter(
        (t) => t.organisation_id === Number(data.organisation_id) && (!team || String(t.id) !== String(team.id)),
      )
    : []

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const options = {
      onSuccess: () => {
        onOpenChange(false)
        reset()
      },
    }
    if (isEdit) {
      patch(`/teams/${team!.id}`, options)
    } else {
      post("/teams", options)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEdit ? `Modifier ${team!.name}` : "Nouvelle équipe"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
            <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dlg_team_name">Nom</Label>
              <Input
                id="dlg_team_name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                required
                maxLength={120}
              />
            </div>

            {current_user?.is_super_admin && (
              <div className="space-y-2">
                <Label htmlFor="dlg_team_organisation">Organisation</Label>
                <Select
                  value={data.organisation_id}
                  onValueChange={(v) => {
                    setData("organisation_id", v)
                    setData("parent_team_id", "")
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    {organisations.map((org) => (
                      <SelectItem key={org.id} value={String(org.id)}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {filteredTeams.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="dlg_parent_team">Équipe parente (optionnel)</Label>
                <Select value={data.parent_team_id} onValueChange={(v) => setData("parent_team_id", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Aucune" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredTeams.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            </DialogBody>
            <DialogFooter>
              {isEdit && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="sm:mr-auto"
                >
                  Supprimer
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={processing}>
                {isEdit ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'équipe</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer l'équipe « {team?.name} » ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
